import type {
  ProblemScoringForViewOption,
  ProblemScoringViewOption,
} from '../@trait/ProblemScoringViewOption.trait'

type ViewOption = ProblemScoringViewOption<'학습모듈' | 'NOT_학습모듈'>

/** 푸터 답안 입력 위젯 노출 — 미제출 + 채점가능. 학부모는 절대 입력 불가. */
export const shouldShowAnswerInput = (
  viewOption: ViewOption,
  problemScoring: ProblemScoringForViewOption,
) => {
  if (viewOption.isParentView) return false
  return !problemScoring.isSubmitted && !viewOption.채점불가능
}

/**
 * Drawer 내부 라벨(풀이동영상/내풀이/해설) 노출 가용성.
 *
 * - 자기주도학습: 풀이동영상·내풀이 슬롯은 컨텐츠 보장 (해설은 문제해설이미지 필요).
 *   단, 학생이 제출하기 전까진 컨텐츠 0 으로 본다 (기존 정책 유지).
 * - 학생/학부모: 각 라벨이 실제 노출 가능할 때만 true.
 */
export const getDrawerLabelVisibility = ({
  viewOption,
  problemScoring,
  isSubmittedAnswerAvailable,
}: {
  viewOption: ViewOption
  problemScoring: ProblemScoringForViewOption
  isSubmittedAnswerAvailable: boolean
}) => {
  const 자기주도학습 = viewOption.isSelfLearning
  const 정답해설공개 = isSolutionLabelOpen(viewOption, problemScoring)
  const 문풀동공개 = isProblemVideoLabelOpen(viewOption, problemScoring)
  // 학생 본인 필기 ("내풀이") restriction 차단 시 라벨/섹션 미노출
  const 필기 =
    isSubmittedAnswerAvailable && problemScoring.isSubmitted && !viewOption.isHandwrittenRestricted

  return {
    showVideoLabel: 자기주도학습 || 문풀동공개,
    showHandwritingLabel: 자기주도학습 || 필기,
    showSolutionLabel: (자기주도학습 || 정답해설공개) && !!problemScoring.문제해설이미지,
  }
}

/**
 * 푸터 Drawer 진입 링크 노출 — "내가 쓴 답·풀이동영상·내풀이·해설" 묶음.
 * 채점 단계(자기주도 → 채점전 → 채점후)별 분기.
 *
 * 학부모는 "내가 쓴 답" / "정답 미입력" 배지 표기를 학생 학습 결과와 무관하게 항상
 * 보여줘야 하므로 푸터를 항상 렌더한다. drawer trigger(">") 자체 노출은 Footer 안의
 * showDrawerTrigger(=라벨 가용성 OR) 가 별도 결정 — 라벨 0 이면 trigger 만 미노출.
 */
export const shouldShowDrawerButton = ({
  viewOption,
  problemScoring,
  isSubmittedAnswerAvailable,
}: {
  viewOption: ViewOption
  problemScoring: ProblemScoringForViewOption
  isSubmittedAnswerAvailable: boolean
}) => {
  if (viewOption.content.type === 'CONCEPTUAL') return false
  if (viewOption.isParentView) return true

  const { isSubmitted, isAutoScoring } = problemScoring
  const studentAppSetting = viewOption.studentAppSetting

  // 자기주도학습: 제출된 경우만 노출
  if (!studentAppSetting) return isSubmitted

  // === 채점 전 ===
  if (!isSubmitted) {
    // 자동채점: 채점 전 노출 없음
    if (isAutoScoring) return false
    // 일반채점: 문풀동 공개 + 정답 비공개
    return studentAppSetting.채점전문풀동공개 && !studentAppSetting.일반채점_채점전정답공개
  }

  // === 채점 후 ===
  // 자동채점: 항상 노출
  if (isAutoScoring) return true
  // 일반채점: 답 있으면 노출, 없으면 공개 조건 따름
  if (isSubmittedAnswerAvailable) return true
  return studentAppSetting.채점후문풀동공개 || studentAppSetting.채점후정답해설공개
}

/**
 * Drawer 진입 라벨 — "해설" 단어 포함 여부 (정답해설 라벨 공개).
 *
 * - 자기주도학습: 별도 라벨 로직 → false
 * - 학부모: 학부모정답해설공개(=채점후정답해설공개) 단독 시그널, 제출/미제출 무관
 * - 학생: 제출 후 채점후정답해설공개 / 제출 전 일반채점_채점전정답공개
 */
export const isSolutionLabelOpen = (
  viewOption: ViewOption,
  problemScoring: ProblemScoringForViewOption,
) => {
  const studentAppSetting = viewOption.studentAppSetting
  if (!studentAppSetting) return false
  if (viewOption.isParentView) return !!studentAppSetting.채점후정답해설공개
  return (
    (problemScoring.isSubmitted && studentAppSetting.채점후정답해설공개) ||
    (!problemScoring.isSubmitted && studentAppSetting.일반채점_채점전정답공개)
  )
}

/**
 * Drawer 진입 라벨 — "풀이동영상" 단어 포함 여부 (문풀동 라벨 공개).
 * 자기주도학습은 별도 라벨 로직을 따르므로 여기서는 false.
 */
export const isProblemVideoLabelOpen = (
  viewOption: ViewOption,
  problemScoring: ProblemScoringForViewOption,
) => {
  const studentAppSetting = viewOption.studentAppSetting
  if (!studentAppSetting) return false
  return (
    (problemScoring.isSubmitted && studentAppSetting.채점후문풀동공개) ||
    (!problemScoring.isSubmitted && studentAppSetting.채점전문풀동공개)
  )
}
